/* -------------- Enums -------------- */

export enum VolunteersEventType {
    ApplicationRequired = 0,
    ApplicationNotRequired = 1
}

export const volunteersEventTypeLabels: Record<VolunteersEventType, string> = {
	[VolunteersEventType.ApplicationRequired]: "Başvurulu",
	[VolunteersEventType.ApplicationNotRequired]: "Başvurusuz"
};

export const cultures = ["tr-TR", "en-US"] as const;

/* -------------- Text limits -------------- */

// The longest texts the project forms accept: the backend validators' limits, or the database column where it is
// shorter (preview header nvarchar(200) under a 255 check, button text nvarchar(100) under a 500 check, category tag
// nvarchar(50) with no check), so a value the panel lets through is never cut off by the database. All of them are
// sent as multipart form data, so a line break counts twice; the rich texts are counted with their formatting (HTML).
export const EventTextRules = {
	locationTextMaxLength: 50,
	projectDateTextMaxLength: 50,
	categoryTagMaxLength: 50,
	previewHeaderMaxLength: 200,
	previewTextMaxLength: 500,
	detailHeaderMaxLength: 255,
	detailTextMaxLength: 5000,
	durationTextMaxLength: 500,
	customButtonTextMaxLength: 100,
	customButtonUrlMaxLength: 500,
	lineBreaks: "crlf",
} as const;

/* -------------- Common -------------- */

export interface ApiResponse<T> {
    result: T;
    error: { code?: number; message?: string } | string | null;
    isSuccessful: boolean;
}

export interface ListResult<T> {
    items: T[];
}

// Page States
export type OperationStatus =
    | { type: "idle" }
    | { type: "loading"; operation: "add" | "fetch" | "update" | "delete" | "export" }
    | { type: "error"; operation: string; message: string };

/* -------------- Event Preview -------------- */

export interface EventPreviewLocalizable {
    eventPreviewLocalizableId: string;
    culture: string;
    header: string;
    previewText: string;
    imageUrl: string;
    categoryTag?: string;
}

export interface EventPreview {
    eventPreviewId: string;
    displayOrder: number | undefined;
    isFeatured: boolean;
    eventPreviewLocalizable: EventPreviewLocalizable[];
}

/* -------------- Event Detail -------------- */

export interface EventDetailLocalizable {
    eventDetailLocalizableId: string;
    culture: string;
    header: string;
    detailText: string;
    applicantText: string;
    processText: string;
    durationText: string;
    customButtonText: string;
    customButtonUrl: string;
    imageUrl: string;
}

export interface EventDetail {
    eventDetailId: string;
    hasCustomButton: boolean;
    eventDetailLocalizable: EventDetailLocalizable[];
}


/* -------------- Methods -------------- */

// LIST

export const eventStateColors: Record<string, string> = {
	"event-open": "#059669",
	"event-days-till-closed": "#059669",
	"event-one-day-till-closed": "#059669",
	"event-under-review": "#EA580C",
	"event-closed": "#475569",
	"event-stateless": "#4F46E5"
};

export interface EventApplicationStatus {
    stateCode: string;
    header: string;
}

export interface EventListItem {
    eventId: string;
    eventType: VolunteersEventType;
    projectDateText: string;
    projectDate?: string | null;
    locationText: string;
    displayOrder: number;
    isActive: boolean;
    header: string;
    applicationStatus?: EventApplicationStatus | null;
    createdDate?: string;
}

export type EventListApiResponse = ApiResponse<ListResult<EventListItem>>;

// GET
export interface EventDetailItem {
    eventId: string;
    eventType: VolunteersEventType;
    projectDateText: string;
    projectDate?: string | null;
    locationText: string;
    isAppliable: boolean;
    isActive: boolean;
    eventPreview: EventPreview;
    eventDetail: EventDetail;
    eventTagIds: string[];
}

export type EventGetApiResponse = ApiResponse<EventDetailItem>;

/* -------------- ADD (POST request, partial - only changed fields) -------------- */
export interface EventPreviewLocalizableFormData {
    culture: string;
    header: string;
    previewText: string;
    image: File | null;
    categoryTag?: string;
}

export const defaultEventPreviewLocalizableData: EventPreviewLocalizableFormData[] = cultures.map(culture => ({
	culture,
	header: "",
	previewText: "",
	image: null,
	categoryTag: "",
}));

export interface EventPreviewFormData {
    displayOrder?: number;
    isFeatured: boolean | undefined;
    eventPreviewLocalizable: EventPreviewLocalizableFormData[];
}

export interface EventDetailLocalizableFormData {
    culture: string;
    header: string;
    detailText: string;
    applicantText: string;
    processText: string;
    durationText: string;
    customButtonText: string;
    customButtonUrl: string;
    image: File | null;
}


export const defaultEventDetailLocalizableData: EventDetailLocalizableFormData[] = cultures.map(culture => ({
	culture,
	header: "",
	detailText: "",
	applicantText: "",
	processText: "",
	durationText: "",
	customButtonText: "",
	customButtonUrl: "",
	image: null,
}));
export interface EventDetailFormData {
    hasCustomButton: boolean | undefined;
    eventDetailLocalizable: EventDetailLocalizableFormData[];
}

export interface EventFormData {
    eventType: VolunteersEventType | undefined;
    projectDateText?: string;
    projectDate?: string | null;
    locationText?: string;
    categoryTag?: string;
    isAppliable: boolean | undefined;
    isActive: boolean | undefined;
    eventPreview: EventPreviewFormData;
    eventDetail: EventDetailFormData;
    eventTagIds: string[];
}

export const defaultEventFormData: EventFormData = {
	eventType: undefined,
	projectDateText: "",
	projectDate: null,
	locationText: "",
	categoryTag: "",
	isAppliable: undefined,
	isActive: undefined,
	eventPreview: {
		displayOrder: undefined,
		isFeatured: undefined,
		eventPreviewLocalizable: defaultEventPreviewLocalizableData,
	},
	eventDetail: {
		hasCustomButton: undefined,
		eventDetailLocalizable: defaultEventDetailLocalizableData,
	},
	eventTagIds: [],
};

// -------------- EDIT (form state, complete) --------------
export type EventEditFormData = EventDetailItem;

/* -------------- UPDATE (PATCH request, partial - only changed fields) -------------- */

export interface UpdateEventPreviewLocalizable {
    eventPreviewLocalizableId: string;
    culture: string;
    header?: string;
    previewText?: string;
    image: File | null;
    categoryTag?: string;
}

export interface UpdateEventPreview {
    eventPreviewId: string;
    displayOrder?: number;
    isFeatured?: boolean;
    eventPreviewLocalizable?: UpdateEventPreviewLocalizable[];
}

export interface UpdateEventDetailLocalizable {
    eventDetailLocalizableId: string;
    culture: string;
    header?: string;
    detailText?: string;
    applicantText?: string;
    processText?: string;
    durationText?: string;
    customButtonText?: string;
    customButtonUrl?: string;
    image: File | null;
}

export interface UpdateEventDetail {
    eventDetailId: string;
    hasCustomButton?: boolean;
    eventDetailLocalizable?: UpdateEventDetailLocalizable[];
}

export interface UpdateEventRequest {
    eventId: string;
    eventType?: VolunteersEventType;
    projectDateText?: string;
    projectDate?: Date | string | null;
    locationText?: string;
    categoryTag?: string;
    isAppliable?: boolean;
    isActive?: boolean;
    eventPreview?: UpdateEventPreview;
    eventDetail?: UpdateEventDetail;
    eventTagIds?: string[];
}

